import { combineReducers, configureStore } from "@reduxjs/toolkit";
import {
    persistReducer,
    persistStore,
} from "redux-persist";
import storage from "redux-persist-indexeddb-storage";
import storageSession from "redux-persist/lib/storage/session";

import AccessLogReducer from "../state/system/AccessLog";
import ActivityReducer from "../state/administration/Activity";
import AdminActivityReducer from "../state/administration/AdminActivity";
import AlertReducer from "../state/system/Alerts";
import ApprovalsReducer from "../state/administration/Approvals";
import CampusReducer from "../state/core/Campus";
import OrganisersReducer from "./organiser/Organisers";
import ClientSystemReducer from "../state/core/ClientSystem";
import Configuration from "../foundation/Configuration";
import ContactReducer from "../state/core/Contact";
import DashboardReducer from "../state/core/Dashboard";
import DisplayReducer from "../state/system/Display";
import EmailLogReducer from "../state/system/EmailLog";
import EmailServerReducer from "../state/core/EmailServer";
import ExportsReducer from "../state/administration/Exports";
import FileExplorerReducer from "../state/system/FileExplorers";
import FoldersReducer from "../state/files/Folders";
import GeographyReducer from "../state/core/Geography";
import GroupsReducer from "../state/archive/Groups";
import ImportsReducer from "../state/files/Imports";
import IntegrationReducer from "../state/core/Integrations";
import LissLogReducer from "../state/system/LissLog";
import LogArchiveReducer from "../state/system/LogArchives";
import LogReducer from "../state/system/Logs";
import MenuReducer from "../state/system/Menu";
import MessageLogReducer from "../state/system/MessageLog";
import MessagingReducer from "../state/system/Messaging";
import OrgUnitsReducer from "../state/core/OrgUnits";
import PermissionsReducer from "../state/system/Permissions";
import ProductSubscriptionsReducer from "../state/core/ProductSubscriptions";
import PublishedTimetableReducer from "./archive/Timetables";
import Recommendaitons from "../state/administration/Recommendations";
import RecycledReducer from "../state/system/Recycled";
import SSOReducer from "../state/core/SSO";
import SchoolReducer from "../state/core/School";
import SessionReducer from "../state/system/Session";
import SettingReducer from "./system/Settings";
import SifLogReducer from "../state/system/SifLog";
import StudentReducer from "../state/core/Students";
import TeacherReducer from "../state/core/Teachers";
import RoomsReducer from "../state/core/Rooms";
import AreaReducer from "../state/core/Areas";
import FacultyReducer from "../state/core/Faculties";
import SubjectReducer from "../state/core/Subjects";
import TabsReducer from "../state/system/Tabs";
import UIConfigurationReducer from "../state/system/UIConfiguration";
import UploadsReducer from "../state/files/Uploads";
import UrlsReducer from "../state/system/Urls";
import UserReducer from "../state/core/User";
import UserRolesReducer from "../state/core/UserRoles";
import YearLevelReducer from "./core/YearLevel";

const storageKey = Configuration.appName + "-v" + Configuration.version;

const dbReducer = combineReducers({
    accessLog: AccessLogReducer,
    activity: ActivityReducer,
    adminActivity: AdminActivityReducer,
    alert: AlertReducer,
    approvals: ApprovalsReducer,
    campus: CampusReducer,
    organisers: OrganisersReducer,
    clientSystem: ClientSystemReducer,
    contact: ContactReducer,
    dashboard: DashboardReducer,
    display: DisplayReducer,
    emailLog: EmailLogReducer,
    emailServers: EmailServerReducer,
    exports: ExportsReducer,
    fileExplorer: FileExplorerReducer,
    folders: FoldersReducer,
    geography: GeographyReducer,
    groups: GroupsReducer,
    imports: ImportsReducer,
    integrations: IntegrationReducer,
    lissLogs: LissLogReducer,
    logArchives: LogArchiveReducer,
    logs: LogReducer,
    menu: MenuReducer,
    messageLog: MessageLogReducer,
    messaging: MessagingReducer,
    orgUnits: OrgUnitsReducer,
    permissions: PermissionsReducer,
    productSubscription: ProductSubscriptionsReducer,
    recommendations: Recommendaitons,
    recycled: RecycledReducer,
    school: SchoolReducer,
    settings: SettingReducer,
    sifLogs: SifLogReducer,
    sso: SSOReducer,
    student: StudentReducer,
    teacher: TeacherReducer,
    room: RoomsReducer,
    area: AreaReducer,
    faculty: FacultyReducer,
    subject: SubjectReducer,
    tabs: TabsReducer,
    timetable: PublishedTimetableReducer,
    ui: UIConfigurationReducer,
    uploads: UploadsReducer,
    urls: UrlsReducer,
    user: UserReducer,
    userRoles: UserRolesReducer,
    yearLevel: YearLevelReducer,
});

const dbPersistConfig = {
    key: "tts",
    storage: storage(storageKey),
    blacklist: ["menu", "alert", "fileExplorer", "recycled"],
    serialize: false,
    deserialize: false,
};

export type IDBState = ReturnType<typeof dbReducer>;
const rootDBReducer = (state: IDBState, action) => {
    // Clear all data in redux store to initial.
    if (action.type === "RESET_SESSION") {
        const { ui, display } = state;

        return dbReducer({ ui, display } as IDBState, action);
    }

    return dbReducer(state, action);
};

const sessionReducer = combineReducers({
    session: SessionReducer,
});

const sessionPersistConfig = {
    key: "session",
    storage: storageSession,
    serialize: true,
    deserialize: true,
};
export type ISessionState = ReturnType<typeof sessionReducer>;
const rootSessionReducer = (state: ISessionState, action) => {
    // Clear all data in redux store to initial.
    if (action.type === "RESET_SESSION") {
        return sessionReducer({ ...state }, action);
    }

    return sessionReducer(state, action);
};

const appReducer = combineReducers({
    db: persistReducer(dbPersistConfig, rootDBReducer),
    session: persistReducer(sessionPersistConfig, rootSessionReducer),
});

const store = configureStore({
    reducer: appReducer,
    middleware: getDefaultMiddleware =>
        getDefaultMiddleware({
            serializableCheck: false,
        }),
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type IAppState = ReturnType<typeof appReducer>;
export type AppDispatch = typeof store.dispatch;

export const persistor = persistStore(store);

export const resetStore = async () => {
    store.dispatch({ type: "RESET_SESSION" });
    await persistor.flush();
};

export default store;
